import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getThirdPartyAgentColor } from '@atlaskit/agent-color/get-third-party-agent-color';
import type { SizeType } from '@atlaskit/avatar/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { ResolvedAgentAvatar } from '@atlaskit/rovo-agent-components/ui/agent-avatar/ResolvedAgentAvatar';

import type { TelepointerAgentKind } from './telepointerPresentation';

/** Rovo presents under its own brand name, so this label is deliberately not translated. */
const ROVO_LABEL = 'Rovo';

const messages = defineMessages({
	externalAgentFallback: {
		id: 'editor-plugin-ai.TelepointerAvatar.TelepointerAvatar.externalAgentFallback',
		defaultMessage: 'External agent',
		description:
			"Badge label on the AI streaming telepointer shown when an agent can't be identified.",
	},
});

export interface TelepointerAvatarProps {
	/** The agent's id. One of the three inputs shared agent-avatar resolution keys on. */
	agentId?: string;
	/** Atlassian identity account id or ARI for the agent. */
	agentIdentityAccountId?: string | null;
	/** Agent display name. Shown for third-party agents, and the avatar's accessible label. */
	agentName?: string;
	/**
	 * The agent's `external_config_reference`. Highest-priority avatar input, and what
	 * gives out-of-the-box and third-party agents their own avatar.
	 */
	agentNamedId?: string;
	/**
	 * Which badge treatment to render. Resolved by the producer so this component stays
	 * data driven and never reads a gate itself.
	 */
	kind?: TelepointerAgentKind;
}

/**
 * The telepointer badge every state shares: a coloured pill holding a leading icon and
 * optional text. The pill's colour comes from the cursor-line CSS variable the plugin
 * sets, so badge, line and content highlight always share one hue.
 *
 * `isNeutral` switches the text to the default colour for the grey fallback / third-party
 * pill, which is too light to carry inverse text.
 */
const TelepointerBadge = ({
	children,
	isNeutral,
	iconOnly,
	testId,
	textColor,
}: {
	children: React.ReactNode;
	/** First-party (avatar-only) badge: a tight square pill with no label, per the Figma spec. */
	iconOnly?: boolean;
	isNeutral?: boolean;
	testId: string;
	textColor?: string;
}): React.JSX.Element => {
	const className = [
		'ai-in-editor-telepointer-badge',
		isNeutral && 'ai-in-editor-telepointer-badge-neutral',
		iconOnly && 'ai-in-editor-telepointer-badge-icon-only',
	]
		.filter(Boolean)
		.join(' ');
	return (
		<span
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- The telepointer is imperative ProseMirror DOM styled by global CSS in GlobalWrapper; the badge hooks into that same styling.
			className={className}
			data-testid={testId}
			style={{ color: textColor }}
		>
			{children}
		</span>
	);
};

/**
 * The fixed leading slot every badge shares, so the icon or avatar sits at the same size and
 * position across Rovo, fallback, first- and third-party. A first party simply omits the label.
 */
const TelepointerLeading = ({
	children,
	isAvatar,
}: {
	children: React.ReactNode;
	/** Avatars render one size larger than the glyph icons, per the Figma spec. */
	isAvatar?: boolean;
}): React.JSX.Element => (
	<span
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- The telepointer is imperative ProseMirror DOM styled by global CSS in GlobalWrapper; the leading slot hooks into that same styling.
		className={
			isAvatar
				? 'ai-in-editor-telepointer-badge-leading ai-in-editor-telepointer-badge-leading-avatar'
				: 'ai-in-editor-telepointer-badge-leading'
		}
	>
		{children}
	</span>
);

/** The generated / out-of-the-box illustration, used when no account picture is fetched. */
const GeneratedAgentAvatar = ({
	agentId,
	agentIdentityAccountId,
	agentNamedId,
	size = 'xsmall',
}: Pick<TelepointerAvatarProps, 'agentId' | 'agentIdentityAccountId' | 'agentNamedId'> & {
	size?: SizeType;
}): React.JSX.Element => (
	// Decorative: the badge already labels the agent (name text or the accessible fallback
	// label), so the avatar carries no own label to avoid a redundant announcement.
	<AgentAvatar
		agentId={agentId}
		agentIdentityAccountId={agentIdentityAccountId}
		agentNamedId={agentNamedId}
		showBorder={false}
		size={size}
	/>
);

/** Neutral badge shown when none of the agent's identifiers are available. */
const AgentFallbackBadge = ({ label }: { label: string }): React.JSX.Element => (
	<TelepointerBadge isNeutral testId="ai-in-editor-telepointer-fallback-avatar">
		<TelepointerLeading>
			<AiAgentIcon color="currentColor" label="" />
		</TelepointerLeading>
		{label}
	</TelepointerBadge>
);

export const TelepointerAvatar = ({
	agentId,
	agentIdentityAccountId,
	agentName,
	agentNamedId,
	kind,
}: TelepointerAvatarProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	if (kind === 'rovo') {
		// Rovo is provisioned its own identity account, so try its real account picture like any
		// other agent; the shared generated-avatar illustration is the fallback when there's no
		// picture on file (or no account id at all) — the same fallback any other agent gets. The
		// label stays fixed either way.
		return (
			<TelepointerBadge testId="ai-in-editor-telepointer-rovo-avatar">
				<TelepointerLeading isAvatar>
					<ResolvedAgentAvatar
						agentIdentityAccountId={agentIdentityAccountId}
						agentName={ROVO_LABEL}
						fallback={
							<GeneratedAgentAvatar
								agentId={agentId}
								agentIdentityAccountId={agentIdentityAccountId}
								agentNamedId={agentNamedId}
								size="xxsmall"
							/>
						}
						size="xxsmall"
					/>
				</TelepointerLeading>
				{ROVO_LABEL}
			</TelepointerBadge>
		);
	}

	// Resolving the agent is the default, so a caller that supplies identifiers without a
	// treatment still gets an avatar. Any one identifier is enough, which leaves the neutral
	// badge for a genuine absence of all of them. A third party keeps its name and the neutral
	// grey pill; a first party shows the avatar alone on the id-derived accent pill.
	if (kind !== 'fallback' && (agentNamedId || agentIdentityAccountId || agentId)) {
		const isThirdParty = kind === 'agent-third-party';
		const brand =
			isThirdParty &&
			isExperimentEnabled('platform_editor_agent_mentions') &&
			isExperimentEnabled('platform_editor_agent_telepointer_redesign')
				? getThirdPartyAgentColor({ agentNamedId, agentName })
				: undefined;
		return (
			<TelepointerBadge
				isNeutral={isThirdParty}
				textColor={brand?.boldText}
				iconOnly={!isThirdParty}
				testId="ai-in-editor-telepointer-agent-avatar"
			>
				<TelepointerLeading isAvatar>
					<ResolvedAgentAvatar
						agentIdentityAccountId={agentIdentityAccountId}
						agentName={agentName}
						fallback={
							<GeneratedAgentAvatar
								agentId={agentId}
								agentIdentityAccountId={agentIdentityAccountId}
								agentNamedId={agentNamedId}
							/>
						}
						size="xsmall"
					/>
				</TelepointerLeading>
				{isThirdParty ? agentName : null}
			</TelepointerBadge>
		);
	}

	return <AgentFallbackBadge label={formatMessage(messages.externalAgentFallback)} />;
};
